import React, { useEffect,useState } from "react";

import "./Agriculture.css";
import { useNavigate } from "react-router-dom";


// ✅ IMAGE IMPORTS
import farmImg from "../../photo/farm.jpg";        // Foreground image
import bgHero from "../../photo/background.jpg";  // Background image
import farmerImg from "../../photo/farmer.jpg";

import { motion } from "framer-motion";
import { 
  Truck, Sprout, Dog, Gauge, Cpu, Cloud, 
  Monitor, Smartphone,  
} from "lucide-react"; // Matches industry-specific icons


import { CheckCircle2 } from "lucide-react";




const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

export default function Agriculture() {
  const navigate = useNavigate();


  const [activeIndex, setActiveIndex] = useState(0);

  const toggleFAQ = (index) => {
  setActiveIndex(activeIndex === index ? null : index);
  };



  const faqData = [
  {
    question: "What is an IoT-based smart farming system?",
    answer:
      "It is a technology-driven approach that uses sensors, data analytics, and automation to monitor and optimize agricultural operations in real time."
  },
  {
    question: "Is this suitable for remote or hilly areas?",
    answer:
      "Yes, the platform is designed to work in diverse environments, including remote and challenging terrains, with reliable connectivity options."
  },
  {
    question: "Can irrigation be automated using this platform?",
    answer:
      "Yes, irrigation can be automated based on real-time soil and weather conditions, ensuring efficient water usage."
  },
  {
    question: "Is this solution suitable for small farms?",
    answer:
      "Yes, the platform is scalable and adaptable, supporting everything from small farms to large agricultural operations based on your needs."
  },
  {
    question: "What types of sensors are supported?",
    answer:
      "The system supports a wide range of sensors, including soil moisture, temperature, humidity, weather, and livestock monitoring devices."
  },
   {
    question: "How does the system help reduce crop loss?",
    answer:
      "By providing real-time alerts and predictive insights, the system helps detect risks early and take preventive actions to minimize losses."
  },

  {
    question: "Can multiple farm locations be managed centrally?",
    answer:
      "Yes, you can monitor and manage multiple farms from a single dashboard for better control and visibility."
  }
];

  const handleEnquiry = () => {
    navigate("/enquiry");
  };

  const benefits = [
    "Maximize production with Precision",
    "Improve Crop and Livestock Quality",
    "Enhance Predictability and Control",
    "Reduce Water Wastage and Energy Use",
    "Decrease Crop Loss and Drought Risk",
    "Enable Data-Driven Planning"
  ];

  // Animation variants for the container and items
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0, 
      transition: { duration: 0.5, ease: "easeOut" } 
    }
  };

  useEffect(() => {
    const cards = document.querySelectorAll(".stack-card");
    

    const handleScroll = () => {
      const trigger = window.innerHeight * 0.1;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();

        if (rect.top < trigger) {
          card.classList.add("scrolled");
        } else {
          card.classList.remove("scrolled");
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="agriculture-page">
      {/* Background image applied via inline style to prevent Webpack path errors */}
      <section 
        className="agri-hero" 
        style={{ 
          backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%), url(${bgHero})` 
        }}
      >
        {/* We keep the overlay div for extra control over darkness/contrast */}
        <div className="hero-overlay"></div>

        <div className="agri-container">
          
          {/* LEFT CONTENT */}
          <div className="agri-left">
            <div className="agri-label">
              Smart Farming Monitoring with PowerSense
            </div>

            <h1 className="agri-title">
              IoT in Agriculture to Boost Farm Productivity
            </h1>

            <p className="agri-desc">
              From soil conditions to water usage and plant stress, PowerSense empowers farmers 
              with real-time energy and operational insights. Ensure efficient resource usage 
              and smarter farming decisions.
            </p>

            <button className="agri-btn" onClick={handleEnquiry}>
              Get Started
            </button>
          </div>

          {/* RIGHT VISUAL */}
          <div className="agri-right">
            <div className="agri-image-wrapper">
              <img
                src={farmImg}
                alt="Agriculture Monitoring"
                className="agri-image"
              />

              {/* FLOATING DATA CARD */}
              <div className="agri-float-card">
                <h4>Land Health</h4>
                <div>Plant Health <span>98%</span></div>
                <div>Water Depth <span>56%</span></div>
                <div>Soil <span>75%</span></div>
                <div>Pest <span>3%</span></div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ================= ✅ NEW: SMART MONITORING (Page 2) ================= */}
      <section className="agri-monitoring section">
        <div className="monitoring-container">
          
          <div className="monitor-inner-card">
            
            {/* LEFT SIDE: Content */}
            <div className="monitor-content">
              <h2 className="monitor-title">
                Transforming Farming Through 
                <span className="accent-text"> Smart Monitoring</span>
              </h2>
              
              <p className="monitor-desc">
                Farming has evolved beyond instincts; it now demands data and automation. 
                As climate change and food demand rise, digital transformation in agriculture is critical. 
                PowerSense offers real-time visibility into soil, crops, water, and livestock across operations. 
                From greenhouses to open fields, we help you farm smarter, sustainably, and more efficiently.
              </p>
              
              {/* Secondary CTA Button (Ghost style like the example) */}
              <button className="agri-btn-secondary" onClick={handleEnquiry}>
                Talk to Our IoT Experts
              </button>
            </div>

            {/* RIGHT SIDE: Farmer Image */}
            <div className="monitor-visual">
              <img 
                src={farmerImg} 
                alt="Farmer monitoring soil" 
                className="farmer-image animated-float"
              />
            </div>
          </div>
        </div>
      </section>

{/* ================= ✅ PAGE 3: SYSTEM ARCHITECTURE ================= */}
      <section className="portal-section flow-section agri-arch-section">
            <div className="flow-canvas-wrapper">
              <div className="container text-center">
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                  <h2 className="flow-header text-white">System Architecture of IoT Agriculture Solution</h2>
                  <p className="flow-subtext">Real-time telemetry from field hardware to secure enterprise cloud</p>
                </motion.div>
                
                <div className="architecture-horizontal">
                  
                  {/* ⚡ MASTER SVG PIPELINE */}
                  <div className="master-svg-container">
                    <svg viewBox="0 0 1000 200" preserveAspectRatio="none" className="master-svg">
                      <defs>
                        <filter id="glow-line">
                          <feGaussianBlur stdDeviation="3" result="blur" />
                          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                        </filter>
                      </defs>

                      {/* Static Tracks */}
                      <path d="M 50 40 L 150 40 Q 200 40 250 100 L 950 100" className="path-track-full" />
                      <path d="M 50 100 L 950 100" className="path-track-full" />
                      <path d="M 50 160 L 150 160 Q 200 160 250 100 L 950 100" className="path-track-full" />

                      {/* Animated Pulses */}
                      {[40, 100, 160].map((startY, i) => (
                        <motion.path
                          key={i}
                          d={`M 50 ${startY} L 150 ${startY} ${startY === 100 ? "" : "Q 200 " + startY} 250 100 L 950 100`}
                          className="path-pulse-full agri-pulse"
                          initial={{ pathLength: 0.1, pathOffset: 0 }}
                          animate={{ pathOffset: 1 }}
                          transition={{
                            duration: 4,
                            ease: "linear",
                            repeat: Infinity,
                            delay: i * 1.2
                          }}
                        />
                      ))}
                    </svg>
                  </div>

                  {/* 🧊 NODES */}
                  <div className="node-group">
                    <IconPod Icon={Truck} label="Vehicles" color="blue-icon" />
                    <IconPod Icon={Sprout} label="Plants" color="green-icon" />
                    <IconPod Icon={Dog} label="Cattle" color="purple-icon" />
                  </div>

                  <div className="node-single node-z">
                    <div className="pod-meter meter-accent-agri">
                      <Gauge size={32} />
                      <span>Smart Sensors</span>
                    </div>
                  </div>

                  <div className="node-single node-z">
                    <div className="protocol-tag-agri">HTTPS / MQTT</div>
                    <div className="pod-control gateway-accent-agri">
                      <Cpu size={40} />
                      <span>IoT Gateway</span>
                    </div>
                  </div>

                  <div className="output-row-glass node-z">
                    <div className="pod-cloud cloud-accent-agri">
                      <Cloud size={40} />
                      <span>PowerSense Cloud</span>
                    </div>
                    <div className="pod-output-group">
                      <div className="pod-output web-accent"><Monitor size={20} /><span>Web Dash</span></div>
                      <div className="pod-output app-accent"><Smartphone size={20} /><span>Mobile App</span></div>
                    </div>
                  </div>

                </div>
                
                {/* Progress labels */}
                <div className="arch-label-row">
                  {["Sources", "Sensors", "Gateway", "Connectivity", "IoT Cloud", "IoT Platform"].map((label, index) => (
                    <span key={index} className="arch-step-tag">{label}</span>
                  ))}
                </div>
              </div>
            </div>
      </section>

{/* ================= ✅ PAGE 4: BENEFITS ================= */}
    <section className="agri-benefits-section">
      <div className="container">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-60"
        >
          <h2 className="benefits-title">Benefits of IoT in Agriculture</h2>
          <div className="title-underline"></div>
        </motion.div>

        <motion.div 
          className="benefits-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {benefits.map((benefit, index) => (
            <motion.div 
              key={index} 
              className="benefit-card"
              variants={itemVariants}
              whileHover={{ scale: 1.02, backgroundColor: "rgba(34, 197, 94, 0.1)" }}
            >
              <div className="benefit-icon-wrapper">
                <CheckCircle2 className="benefit-check-icon" size={24} />
              </div>
              <span className="benefit-text">{benefit}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>

{/* ================= STACKING SECTION START ================= */}
      <section className="stack-section">
        
        <div className="stack-title-block">
          <h2 className="master-arch-title">An End-to-End Smart Farming Solutions</h2>
          <div className="title-glow-line"></div>
        </div>

        {/* CARD 1: PRECISION FARMING */}
        <div className="stack-card precision-card">
          <div className="precision-grid">
            <div className="precision-content">
              <span className="precision-chip" style={{background: "#633bad"}}>Soil Intelligence</span>
              <h3 className="precision-title">Advanced Soil Intelligence</h3>
              <p className="precision-desc">
                Gain deeper visibility into soil conditions with continuous monitoring and smart analytics.
                 Our platform tracks moisture, temperature, and nutrient balance in real time,
                  enabling accurate irrigation and fertilization decisions.
                   Data-driven insights help improve crop performance, reduce waste,
                    and maintain optimal growing conditions.
              </p>
              <ul className="precision-list">
                <li><div className="bullet-dot" style={{borderColor: "#633bad"}}><div className="inner-dot" style={{background: "#633bad"}}></div></div> Soil Health Monitoring</li>
                <li><div className="bullet-dot" style={{borderColor: "#633bad"}}><div className="inner-dot" style={{background: "#633bad"}}></div></div>  Nutrient Level Tracking</li>
                <li><div className="bullet-dot" style={{borderColor: "#633bad"}}><div className="inner-dot" style={{background: "#633bad"}}></div></div> Smart Irrigation Based on Soil Data</li>
              </ul>
            </div>
            <div className="precision-visual">
              <div className="floating-stack">
                <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 5, repeat: Infinity }} className="stack-img img-top">
                  <img src={require("../../photo/agri4.png")} alt="Air" />
                </motion.div>
                <motion.div animate={{ y: [0, 15, 0] }} transition={{ duration: 6, repeat: Infinity }} className="stack-img img-bottom">
                  <img src={require("../../photo/agri41.png")} alt="IAQ" />
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* CARD 2: WEATHER PROTECTION */}
        <div className="stack-card weather-card">
          <div className="weather-grid">
            <div className="weather-visual">
               <div className="floating-stack">
                <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 7, repeat: Infinity }} className="stack-img img-top">
                  <img src={require("../../photo/agriweather.png")} alt="Temp" />
                </motion.div>
                <motion.div animate={{ y: [0, 15, 0] }} transition={{ duration: 5, repeat: Infinity }} className="stack-img img-bottom">
                  <img src={require("../../photo/agritemp.png")} alt="Weather" />
                </motion.div>
              </div>
            </div>
            <div className="weather-content">
              <span className="weather-chip" style={{background: "#1d91b1db"}}>Smart Farming Insights</span>
              
              <h3 className="weather-title">Climate Smart Crop Protection</h3>
              <p className="weather-desc">
                Take control of your crops with powerful, data-driven insights. 
                Our system transforms weather and environmental data into actionable decisions,
                 helping you protect your crops, boost productivity, and reduce uncertainty.
                 Whether it's open fields or controlled environments,
              </p>
              <ul className="weather-list">
                <li><div className="bullet-dot" style={{borderColor: "#1d91b1db"}}><div className="inner-dot" style={{background: "#1d91b1db"}}></div></div> Live Climate Insights</li>
                <li><div className="bullet-dot" style={{borderColor: "#1d91b1db"}}><div className="inner-dot" style={{background: "#1d91b1db"}}></div></div> Smart Environmental Tracking</li>
                <li><div className="bullet-dot" style={{borderColor: "#1d91b1db"}}><div className="inner-dot" style={{background: "#1d91b1db"}}></div></div> Predictive Weather Intelligence</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CARD 3: POST-HARVEST STORAGE */}
        <div className="stack-card storage-card">
          <div className="precision-grid">
            <div className="precision-content">
              <span className="precision-chip" style={{background: "#983d6f"}}>Storage Monitoring</span>
              <h3 className="precision-title">Quality & Freshness Control</h3>
              <p className="precision-desc">
                Ensure post-harvest quality and reduce spoilage by monitoring storage conditions 
                in real time. Continuous tracking of temperature, humidity, CO₂, and ammonia 
                levels inside silos, cold rooms, or vegetable storage units.
              </p>
              <ul className="precision-list">
                <li><div className="bullet-dot" style={{borderColor: "#983d6f"}}><div className="inner-dot" style={{background: "#983d6f"}}></div></div> Cold Room Temperature Tracking</li>
                <li><div className="bullet-dot" style={{borderColor: "#983d6f"}}><div className="inner-dot" style={{background: "#983d6f"}}></div></div> Ethylene & Gas Detection</li>
                <li><div className="bullet-dot" style={{borderColor: "#983d6f"}}><div className="inner-dot" style={{background: "#983d6f"}}></div></div> Real-time Spoilage Alerts</li>
              </ul>
            </div>
            <div className="precision-visual">
              <div className="floating-stack">
               
                {/* TOP IMAGE */}
                <motion.div animate={{ y: [0, 15, 0] }} transition={{ duration: 5, repeat: Infinity }} className="stack-img img-top">
                  <img src={require("../../photo/agristore1.png")} alt="Odour" />
                </motion.div>

                {/* MIDDLE IMAGE */}
                <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 5, repeat: Infinity }} className="stack-img img-mid">
                  <img src={require("../../photo/agristore2.png")} alt="Inventory" />
                </motion.div>

                {/* BOTTOM IMAGE */}
                <motion.div animate={{ y: [0, 15, 0] }} transition={{ duration: 6, repeat: Infinity }} className="stack-img img-bottom">
                  <img src={require("../../photo/agristore.png")} alt="Inventory" />
                </motion.div>
              </div>
              
            </div>
          </div>
        </div>

        {/* CARD 4: crop plant health MANAGEMENT */}
        <div className="stack-card livestock-card">
          <div className="weather-grid">
            <div className="weather-visual">
               <div className="floating-stack">
                <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 5, repeat: Infinity }} className="stack-img img-top">
                  <img src={require("../../photo/agricrop3.png")} alt="Drone" />
                </motion.div>
                <motion.div animate={{ y: [0, 15, 0] }} transition={{ duration: 6, repeat: Infinity }} className="stack-img img-bottom">
                  <img src={require("../../photo/agricrop2.png")} alt="Automation" />
                </motion.div>
              </div>
            </div>
            <div className="weather-content">
              <span className="weather-chip" style={{background: "#98a131"}}>Crop & Plant Health Monitoring</span>
              <h3 className="weather-title">Crop Performance & Yield Optimization</h3>
              <p className="weather-desc">
                Identify plant stress early and take action before it affects productivity.
                 Monitor crop growth stages, disease indicators, and environmental stress factors in real time.
                  Timely insights help improve crop quality, increase yield, and minimize losses.
              </p>
              <ul className="weather-list">
                <li><div className="bullet-dot-blue" style={{borderColor: "#98a131"}}><div className="inner-dot-blue" style={{background: "#98a131"}}></div></div> Crop Growth Monitoring</li>
                <li><div className="bullet-dot-blue" style={{borderColor: "#98a131"}}><div className="inner-dot-blue" style={{background: "#98a131"}}></div></div> Stress & Disease Detection</li>
                <li><div className="bullet-dot-blue" style={{borderColor: "#98a131"}}><div className="inner-dot-blue" style={{background: "#98a131"}}></div></div> Vegetation Analysis</li>
                <li><div className="bullet-dot-blue" style={{borderColor: "#98a131"}}><div className="inner-dot-blue" style={{background: "#98a131"}}></div></div> Yield Prediction</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CARD 5: AUTONOMOUS OPERATIONS */}
        <div className="stack-card autonomous-card">
          <div className="precision-grid">
            <div className="precision-content">
              <span className="precision-chip" style={{background: "#10b981"}}>🐄 Livestock Monitoring</span>
              <h3 className="precision-title">Animal Health & Performance Management</h3>
              <p className="precision-desc">
                Track animal behavior, movement, and vital parameters to ensure overall health and safety.
                Continuously monitor body temperature, activity levels, and feeding patterns in real time.
                Detect early signs of illness, stress, or irregular behavior with intelligent alerts.
                Enable timely veterinary care, improve productivity, and reduce livestock losses.
              </p>
              <ul className="precision-list">
                <li><div className="bullet-dot" style={{borderColor: "#10b981"}}><div className="inner-dot" style={{background: "#10b981"}}></div></div> Vital Signs & Body Temperature Tracking</li>
                <li><div className="bullet-dot" style={{borderColor: "#10b981"}}><div className="inner-dot" style={{background: "#10b981"}}></div></div> Feeding Patterns & Movement Analysis</li>
                <li><div className="bullet-dot" style={{borderColor: "#10b981"}}><div className="inner-dot" style={{background: "#10b981"}}></div></div> Early Health Alerts & Intervention</li>
              </ul>
            </div>
            <div className="precision-visual">
              <div className="floating-stack">
                <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 5, repeat: Infinity }} className="stack-img img-top">
                  <img src={require("../../photo/agrianimal.png")} alt="Drone" />
                </motion.div>
                <motion.div animate={{ y: [0, 15, 0] }} transition={{ duration: 6, repeat: Infinity }} className="stack-img img-bottom">
                  <img src={require("../../photo/agrianimal2.png")} alt="Automation" />
                </motion.div>
              </div>
            </div>
          </div>
        </div>

      </section>

            {/* ================= FINAL CTA SECTION ================= */}

      <section className="ups-final">

        <div className="final-content">

          <h2>
            Would you like to see smart farming in action <br />
            
          </h2>

          <p>Get in touch with our team today!</p>

          <button 
            className="final-btn"
            onClick={() => navigate("/enquiry")}
          >
            GET STARTED
          </button>

        </div>

      </section>

      

      {/* ================= FAQ SECTION ================= */}

      <section className="faq-section">

        <h2>Frequently asked questions</h2>

        <div className="faq-container">

          {faqData.map((item, index) => (
            <div 
              key={index} 
              className={`faq-item ${activeIndex === index ? "active" : ""}`}
              onClick={() => toggleFAQ(index)}
            >
              <div className="faq-question">
                <span>{item.question}</span>
                <span className="arrow">
                  {activeIndex === index ? "−" : "+"}
                </span>
              </div>

              <div className="faq-answer">
                <p>{item.answer}</p>
              </div>
            </div>
          ))}

        </div>

      </section>







    </div>
  );
}

function IconPod({ Icon, label, color }) {
  return (
    <motion.div whileHover={{ y: -5 }} className={`pod-source ${color}`}>
      <div className="icon-circle"><Icon size={20} /></div>
      <span className="label-sm">{label}</span>
    </motion.div>
  );
}